import React, { useEffect, useMemo, useState } from "react";
import { capi, api, apiErr, fileUrl } from "@/lib/api";
import { formatGs, LOCATION_NAMES } from "@/lib/format";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Coffee, ShoppingBag, Plus, Minus, X, User, QrCode, Store, Clock, LogOut, Package, Sun, Moon } from "lucide-react";

export default function PublicOrder() {
  const [theme, setTheme] = useState(localStorage.getItem("nauta_theme") || "dark");
  const [loc, setLoc] = useState("sabornauta");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [cartOpen, setCartOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [pickup, setPickup] = useState("");
  const [notes, setNotes] = useState("");
  const [info, setInfo] = useState({ bank_info: "", store_hours: "" });
  const [myOrders, setMyOrders] = useState([]);

  useEffect(() => { document.documentElement.classList.toggle("dark", theme === "dark"); localStorage.setItem("nauta_theme", theme); }, [theme]);
  useEffect(() => { api.get("/public/info").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  useEffect(() => { capi.get("/customers/me").then((r) => setCustomer(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/public/products", { params: { location: loc } }).then((r) => setProducts(r.data)).catch(() => {}); }, [loc]);
  useEffect(() => { if (customer) capi.get("/orders/mine").then((r) => setMyOrders(r.data)).catch(() => {}); }, [customer]);

  const cats = useMemo(() => Array.from(new Set(products.map((p) => p.category))), [products]);

  const add = (p) => setCart((c) => { const ex = c.find((x) => x.product_id === p.id); if (ex) return c.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x); return [...c, { product_id: p.id, name: p.name, price: p.price, quantity: 1 }]; });
  const chg = (id, d) => setCart((c) => c.map((x) => x.product_id === id ? { ...x, quantity: Math.max(1, x.quantity + d) } : x).filter((x) => x.quantity > 0));
  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const doAuth = async (e) => {
    e.preventDefault();
    try {
      const ep = mode === "login" ? "/customers/login" : "/customers/register";
      const { data } = await capi.post(ep, form);
      localStorage.setItem("nauta_ctoken", data.token);
      setCustomer(data.customer); setAuthOpen(false);
      toast.success("¡Bienvenido " + data.customer.name + "!");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const logout = () => { localStorage.removeItem("nauta_ctoken"); setCustomer(null); setMyOrders([]); };

  const placeOrder = async () => {
    if (!customer) { setCartOpen(false); setAuthOpen(true); return; }
    if (!cart.length) return;
    if (!pickup) { toast.error("Elige un horario de retiro"); return; }
    try {
      await capi.post("/orders", { location: loc, pickup_time: pickup, items: cart, notes });
      toast.success("¡Pedido enviado! Te avisaremos cuando esté listo.");
      setCart([]); setCartOpen(false); setNotes(""); setPickup("");
      capi.get("/orders/mine").then((r) => setMyOrders(r.data));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center neon-glow"><Coffee className="w-5 h-5 text-primary-foreground" /></div>
            <span className="font-head font-extrabold text-lg">Nauta<span className="text-primary">POS</span></span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="pub-theme" className="w-9 h-9 rounded-lg border border-border flex items-center justify-center">{theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
            <button onClick={() => setQrOpen(true)} data-testid="pub-qr-btn" className="w-9 h-9 rounded-lg border border-border flex items-center justify-center"><QrCode className="w-4 h-4" /></button>
            {customer ? (
              <button onClick={logout} data-testid="pub-logout" className="flex items-center gap-2 h-9 px-3 rounded-lg bg-secondary text-sm font-medium"><User className="w-4 h-4 text-primary" /> {customer.name.split(" ")[0]} <LogOut className="w-3.5 h-3.5" /></button>
            ) : (
              <button onClick={() => setAuthOpen(true)} data-testid="pub-login-btn" className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold">Ingresar</button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-8">
        <div className="relative rounded-2xl overflow-hidden mb-6 h-44 sm:h-56">
          <img src="https://images.unsplash.com/photo-1736813133600-66b65bcfeacf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="café" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55 flex flex-col justify-center px-6">
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold text-white">Pedí online, retirá en el local</h1>
            <p className="text-white/80 text-sm mt-1">Elegí tu local y armá tu pedido · {info.store_hours || "Lun-Sáb"}</p>
          </div>
        </div>

        {/* Location selector */}
        <div className="flex gap-2 mb-6">
          {Object.keys(LOCATION_NAMES).map((l) => (
            <button key={l} onClick={() => setLoc(l)} data-testid={`pub-loc-${l}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${loc === l ? "bg-primary text-primary-foreground border-primary neon-glow" : "border-border bg-card"}`}>
              <Store className="w-4 h-4" /> {LOCATION_NAMES[l]}
            </button>
          ))}
        </div>

        {customer && myOrders.length > 0 && (
          <div className="card-surface p-4 mb-6">
            <p className="font-head font-bold mb-2 text-sm">Mis pedidos recientes</p>
            <div className="flex gap-2 overflow-x-auto">
              {myOrders.slice(0, 5).map((o) => (
                <div key={o.id} className="shrink-0 bg-secondary rounded-lg p-3 text-xs min-w-[150px]">
                  <p className="font-data text-muted-foreground">#{o.id.slice(0, 6)}</p>
                  <p className="font-semibold capitalize text-primary">{o.status}</p>
                  <p className="font-data">{formatGs(o.total)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products by category */}
        {cats.map((c) => (
          <div key={c} className="mb-8">
            <h2 className="font-head text-xl font-bold mb-3">{c}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {products.filter((p) => p.category === c).map((p) => (
                <div key={p.id} className="card-surface overflow-hidden group" data-testid={`pub-product-${p.id}`}>
                  <div className="aspect-square bg-secondary overflow-hidden">
                    {p.image_url ? <img src={fileUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <Package className="w-8 h-8 m-auto mt-12 text-muted-foreground" />}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-data text-primary font-bold text-sm">{formatGs(p.price)}</span>
                      <button onClick={() => add(p)} data-testid={`pub-add-${p.id}`} className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90"><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {products.length === 0 && <div className="card-surface p-12 text-center text-muted-foreground mb-8">No hay productos disponibles en este local</div>}
      </div>

      {/* Floating cart */}
      {cart.length > 0 && (
        <button onClick={() => setCartOpen(true)} data-testid="pub-cart-btn" className="fixed bottom-6 right-6 z-40 flex items-center gap-3 h-14 px-6 rounded-full bg-primary text-primary-foreground font-semibold neon-glow shadow-xl">
          <ShoppingBag className="w-5 h-5" /> {cart.reduce((s, i) => s + i.quantity, 0)} · {formatGs(total)}
        </button>
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm" onClick={() => setCartOpen(false)}>
          <div className="w-full max-w-md bg-card h-full p-6 overflow-y-auto border-l border-border" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-xl font-bold">Tu pedido</h3><button onClick={() => setCartOpen(false)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-2 mb-4">
              {cart.map((i) => (
                <div key={i.product_id} className="flex items-center gap-2 bg-secondary rounded-lg p-2">
                  <div className="flex-1"><p className="text-sm font-medium">{i.name}</p><p className="font-data text-xs text-muted-foreground">{formatGs(i.price)}</p></div>
                  <button onClick={() => chg(i.product_id, -1)} className="w-7 h-7 rounded bg-background flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                  <span className="w-6 text-center font-semibold">{i.quantity}</span>
                  <button onClick={() => chg(i.product_id, 1)} className="w-7 h-7 rounded bg-background flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="space-y-3 border-t border-border pt-4">
              <div><label className="text-sm font-medium mb-1 flex items-center gap-1"><Store className="w-4 h-4" /> Local de retiro</label><p className="text-sm font-semibold text-primary">{LOCATION_NAMES[loc]}</p></div>
              <div><label className="text-sm font-medium mb-1 flex items-center gap-1"><Clock className="w-4 h-4" /> Horario de retiro</label>
                <input type="time" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="pub-pickup" className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none" /></div>
              <div><label className="text-sm font-medium mb-1 block">Nota (opcional)</label><input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej: sin azúcar" className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none" /></div>
              {info.bank_info && <p className="text-xs text-muted-foreground">Pago en el local o transferencia: {info.bank_info}</p>}
              <div className="flex justify-between font-head font-extrabold text-xl"><span>Total</span><span className="font-data text-primary">{formatGs(total)}</span></div>
              <button onClick={placeOrder} data-testid="pub-place-order" className="w-full h-12 rounded-lg bg-primary text-primary-foreground font-semibold neon-glow">{customer ? "Confirmar pedido" : "Ingresar para pedir"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Auth modal */}
      {authOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setAuthOpen(false)}>
          <div className="card-surface w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-xl font-bold mb-1">{mode === "login" ? "Ingresar" : "Crear cuenta"}</h3>
            <p className="text-sm text-muted-foreground mb-4">Accedé para hacer tu pedido y acumular beneficios</p>
            <form onSubmit={doAuth} className="space-y-3">
              {mode === "register" && <input required placeholder="Nombre completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none" />}
              {mode === "register" && <input placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none" />}
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none" />
              <input required type="password" placeholder="Contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cust-password" className="w-full h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none" />
              <button type="submit" data-testid="cust-submit" className="w-full h-11 rounded-lg bg-primary text-primary-foreground font-semibold neon-glow">{mode === "login" ? "Ingresar" : "Registrarme"}</button>
            </form>
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-center text-sm text-muted-foreground hover:text-primary mt-3">
              {mode === "login" ? "¿No tenés cuenta? Registrate" : "¿Ya tenés cuenta? Ingresá"}
            </button>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setQrOpen(false)}>
          <div className="card-surface w-full max-w-xs p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-lg font-bold mb-3">Guardá en tu celular</h3>
            <div className="bg-white p-4 rounded-xl inline-block"><QRCodeCanvas value={pageUrl} size={180} fgColor="#0A0A0B" /></div>
            <p className="text-xs text-muted-foreground mt-3">Escaneá para abrir el menú online y hacer tu pedido desde cualquier dispositivo.</p>
            <button onClick={() => setQrOpen(false)} className="w-full h-10 rounded-lg bg-secondary mt-4 font-medium">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}
